/** Daily gift, streak, league and daily-puzzle rules (MT-06, CP-01, SO-02). */
import { fnv1a32 } from './hash.js';
import { weekNumber } from './calendar.js';

// ---------- MT-06.1 daily gift: 7-day cycle, resets after 2 missed days ----------
export interface DailyGiftState {
  lastClaimDay: number | null;
  cycleDay: number; // 0..6 of the last claim
}

export function dailyGiftStatus(s: DailyGiftState, today: number): { claimable: boolean; nextCycleDay: number } {
  // same day, or a device clock/timezone that moved backwards: nothing new to claim
  if (s.lastClaimDay !== null && today <= s.lastClaimDay) return { claimable: false, nextCycleDay: s.cycleDay };
  if (s.lastClaimDay === null || today - s.lastClaimDay - 1 >= 2) return { claimable: true, nextCycleDay: 0 };
  return { claimable: true, nextCycleDay: (s.cycleDay + 1) % 7 };
}

export function claimDailyGift(s: DailyGiftState, today: number): { ok: boolean; state: DailyGiftState; giftDay: number } {
  const st = dailyGiftStatus(s, today);
  if (!st.claimable) return { ok: false, state: s, giftDay: s.cycleDay + 1 };
  return { ok: true, state: { lastClaimDay: today, cycleDay: st.nextCycleDay }, giftDay: st.nextCycleDay + 1 };
}

// ---------- MT-06.2 streak with 2 automatic rest days per week ----------
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100] as const;
export const REST_DAYS_PER_WEEK = 2;

export interface StreakState {
  count: number;
  lastDay: number | null;
  restWeek: number; // week number the rest counter refers to
  restUsed: number;
}

export interface StreakUpdate {
  state: StreakState;
  changed: boolean;
  restDaysUsed: number;
  reset: boolean;
  milestone: number | null;
}

export function streakOnPlay(s: StreakState, today: number): StreakUpdate {
  // same day, or travelling west / clock skew: an earlier day never counts as a new streak day
  if (s.lastDay !== null && today <= s.lastDay) return { state: s, changed: false, restDaysUsed: 0, reset: false, milestone: null };
  const next: StreakState = { ...s };
  let restDaysUsed = 0;
  let reset = false;
  if (s.lastDay === null) {
    next.count = 1;
  } else {
    const gap = today - s.lastDay - 1;
    let covered = gap <= 4; // 5+ consecutive days span at most 2 weeks = 4 rest days
    if (covered && gap > 0) {
      let week = next.restWeek;
      let used = next.restUsed;
      for (let d = s.lastDay + 1; d < today; d++) {
        const w = weekNumber(d);
        if (w !== week) {
          week = w;
          used = 0;
        }
        if (used >= REST_DAYS_PER_WEEK) {
          covered = false;
          break;
        }
        used++;
        restDaysUsed++;
      }
      if (covered) {
        next.restWeek = week;
        next.restUsed = used;
      }
    }
    if (covered) next.count = s.count + 1;
    else {
      next.count = 1;
      reset = true;
      restDaysUsed = 0;
    }
  }
  next.lastDay = today;
  const milestone = (STREAK_MILESTONES as readonly number[]).includes(next.count) ? next.count : null;
  return { state: next, changed: true, restDaysUsed, reset, milestone };
}

// ---------- CP-01 weekly league ----------
export const LEAGUE_TIERS = ['bronze', 'silver', 'gold', 'sapphire', 'ruby', 'emerald', 'amethyst', 'diamond'] as const;
export type LeagueTier = (typeof LEAGUE_TIERS)[number];
export const LEAGUE_GROUP_SIZE = 50;

export interface LeagueMember {
  playerId: string;
  stars: number;
  /** epoch ms (or any monotonic int) when the current star total was reached; earlier ranks higher */
  reachedAt: number;
}

export function rankLeague<T extends LeagueMember>(members: readonly T[]): T[] {
  return [...members].sort(
    (a, b) => b.stars - a.stars || a.reachedAt - b.reachedAt || (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0),
  );
}

export type LeagueOutcome = 'promoted' | 'held' | 'demoted' | 'removed';

/** Promotion/demotion band: 10 for full groups (≥45 active), a fifth (≥1) for smaller ones. */
export function leagueBand(activeCount: number): number {
  return activeCount >= 45 ? 10 : Math.max(1, Math.ceil(activeCount / 5));
}

/** Flow I: top 10 promoted, 11–40 held, bottom 10 demoted; scaled for smaller groups. */
export function leagueOutcome(rank: number, activeCount: number, tier: number, stars: number): LeagueOutcome {
  if (stars <= 0) return 'removed';
  const band = leagueBand(activeCount);
  if (rank <= band && tier < LEAGUE_TIERS.length - 1) return 'promoted';
  if (rank > activeCount - band && tier > 0) return 'demoted';
  return 'held';
}

export function nextTier(tier: number, o: LeagueOutcome): number {
  if (o === 'promoted') return Math.min(LEAGUE_TIERS.length - 1, tier + 1);
  if (o === 'demoted') return Math.max(0, tier - 1);
  return tier;
}

// ---------- SO-02 daily puzzle ----------
export function dailySeed(dateKey: string): number {
  return fnv1a32('daily:' + dateKey);
}

/**
 * 7-square emoji grid of the bag history for share cards (Flow F). Trace
 * entries are `bagLen | cap << 8` (the cap at that moment); a bare bagLen uses `cap`.
 */
export function emojiGrid(trace: readonly number[], cap: number, won: boolean): string {
  const squares: string[] = [];
  const pressure = (v: number) => {
    const len = v & 0xff;
    const c = v >> 8 || cap;
    return len <= Math.floor(c / 2) ? 0 : len <= c - 2 ? 1 : len < c ? 2 : 3;
  };
  for (let k = 0; k < 7; k++) {
    const a = Math.floor((k * trace.length) / 7);
    const b = Math.max(a + 1, Math.floor(((k + 1) * trace.length) / 7));
    let peak = 0;
    for (let i = a; i < b && i < trace.length; i++) peak = Math.max(peak, pressure(trace[i]!));
    squares.push(['🟩', '🟨', '🟧', '🟥'][peak]!);
  }
  if (!won) squares[6] = '🟥';
  return squares.join('');
}
